/**
 * Service layer — Meetings domain (meeting minutes)
 *
 * Owns: Meetings (tblsvfCBc3IfHSKym)
 * Reads (does not own): Threads (tblXlbRZkUldgBHkc) + Thread Activity (tblrb79TQB7KlwvdL)
 *   via services/threads.js — a meeting REFERENCES threads (the Meeting↔Thread link), it does
 *   not embed them. The minutes body is "the threads linked to this meeting, grouped by category,
 *   each showing its Include-in-Minutes activity log".
 *
 * The entire data model already exists in Airtable (it drove the legacy Softr + Documint tool).
 * This service is the native AlderCortex entry point that replaces the Softr forms; the PDF is
 * rendered by api/meeting-minutes-pdf.js (replacing Documint) and posting/emailing runs through
 * api/meeting-minutes.js (Resend). See docs/MEETING_MINUTES.md.
 */

import { apiGet, apiGetOne, apiPost, apiPatch, apiDelete } from './_api.js'
import { getProjectThreads } from './threads.js'
import { getProjectDetails } from './projects.js'
import { startDiscussion, replyToThread } from './slack.js'
import { getOrCreateLink, upsertEntry, listEntries } from './journey.js'
import { getProjectPhases, matchPhaseName } from './phases.js'

const TBL_MEETINGS = 'tblsvfCBc3IfHSKym'

/* ── Field-name constants (the service owns these; callers never see them) ───── */
const F = {
  TYPE:        'Type',            // singleSelect (MEETING_TYPES)
  PROJECT:     'Project',         // link → Projects (single-pref)
  DATE:        'Meeting Date',    // dateTime
  END:         'Meeting End',     // dateTime
  LOCATION:    'Location',        // singleSelect (MEETING_LOCATIONS)
  PHASE:       'Phase',           // singleSelect (NOT a link — a phase-name select)
  ATTENDEES:   'Attendees',       // link → Contacts (multi)
  CONSULTANTS: 'Consultants Involved', // multipleSelects
  THREADS:     'Threads',         // link → Threads (multi) — the minutes line items
  STATUS:      'Status',          // singleSelect (MEETING_STATUSES)
  POSTED:      'Posted',          // checkbox
  POSTING_MEMBER: 'Posting Member',   // link → Members (single-pref)
  CREATOR:     'Meeting Creator', // link → Members (single-pref)
  SKIP:        'Skip Minutes',    // checkbox
  POSTED_PDF:  'Posted Meeting Minutes', // attachments — the issued document of record
  NOTES_IMPORTED: 'Notes Imported',   // checkbox — set once a Zoom-notes import is committed (gates re-import)
  SLACK_TS:    'Slack Thread Timestamp', // singleLineText — the meeting's own Slack discussion (see api/slack.js ENTITY_TS_FIELD)
  // read-only / formula helpers
  RECORD_ID:   'Record ID',
  PROJ_UID:    'Project Record UID (from Project)',
  DATE_STR:    'Meeting Date String',
  STATUS_MSG:  'Status Message',        // AGENDA when date is future, else Status
  STAGE:       'Minutes Stage Calculated', // 0 Agenda · 1 Drafting · 2 Ready · 3 Approved · 4 Posted · 99 Skip
  ATTENDEE_EMAILS: 'Email',             // lookup of attendee Contact emails
}

/** Meeting types (singleSelect on Meetings.Type) — canonical order for grouping. */
export const MEETING_TYPES = [
  'OAC',
  'Design Progress',
  'Consultant Coordination',
  'Pre Construction',
  'Agency',
  'Neighborhood',
]

/** Meeting locations (singleSelect on Meetings.Location). */
export const MEETING_LOCATIONS = [
  'Video Conference',
  'Phone Call',
  'Project Site',
  'Alder James Studio',
  'Consultant Office',
]

/** Workflow statuses (singleSelect on Meetings.Status). 'AGENDA' is computed, not a stored value. */
export const MEETING_STATUSES = ['DRAFTING', 'DRAFT READY', 'DRAFT APPROVED', 'POSTED', 'SKIP MINUTES']

/** Ordered stage labels for the workflow stepper, indexed by Minutes Stage Calculated (0..4). */
export const MINUTES_STAGE_LABELS = ['Agenda', 'Drafting', 'Draft Ready', 'Draft Approved', 'Posted']

/**
 * The three activity types that appear on the minutes (mirrors the Thread Activity
 * 'Include in Minutes' formula). Action Taken / Reassigned / Deadline Changed / etc. are
 * internal log entries and are NOT rendered on the issued document.
 */
export const MINUTES_ACTIVITY_TYPES = ['Thread Opened', 'Meeting Discussion', 'Thread Closed']

function todayStr() { return new Date().toISOString().slice(0, 10) }
function dayOf(v) { return v ? String(v).slice(0, 10) : '' }

/* ── Reads ──────────────────────────────────────────────────────────────────── */

/**
 * All Meetings for a project, newest first. Filtered by the project record id against the
 * {Project Record UID (from Project)} lookup (same pattern as getProjectThreads).
 */
export function getProjectMeetings(projectId) {
  const formula = 'FIND("' + projectId + '",ARRAYJOIN({' + F.PROJ_UID + '},","))'
  return apiGet(
    TBL_MEETINGS,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=' + encodeURIComponent(F.DATE) + '&sort%5B0%5D%5Bdirection%5D=desc&maxRecords=200'
  )
}

/**
 * The project's meetings whose minutes are POSTED, newest first, as plain rows
 * [{ id, date, type, label }] — the meetings that can stand as a client's written approval
 * (docs/COMPRESSED_CHAIN.md: posted minutes go to the client with a revision window, so they are a
 * written record of what was decided).
 */
export async function getPostedMeetings(projectId) {
  if (!projectId) return []
  const recs = await getProjectMeetings(projectId)
  return (recs || []).filter(r => r.fields && (r.fields[F.POSTED] === true || r.fields[F.STATUS] === 'POSTED') && r.fields[F.SKIP] !== true)
    .map(r => {
      const date = dayOf(r.fields[F.DATE])
      const type = r.fields[F.TYPE] || 'Meeting'
      const nice = date ? new Date(date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''
      return { id: r.id, date, type, label: type + (nice ? ' · ' + nice : '') }
    })
}

/** A single Meeting record { id, fields } or null. */
export function getMeetingById(meetingId) {
  if (!meetingId) return Promise.resolve(null)
  return apiGetOne(TBL_MEETINGS, meetingId)
}

/* ── Writes ─────────────────────────────────────────────────────────────────── */

/**
 * Create a Meeting. Status starts at DRAFTING; if the meeting date is in the future the
 * Status Message formula reports AGENDA automatically (no stored value needed for that).
 *
 * @param {string} projectId
 * @param {object} o
 *   - type {string}            required — MEETING_TYPES
 *   - meetingDate {string}     required — ISO datetime
 *   - meetingEnd {string|null}
 *   - location {string|null}
 *   - phase {string|null}      phase-NAME select value
 *   - attendees {string[]}     Contact record ids
 *   - consultants {string[]}   Consultants Involved select values
 *   - creatorMemberId {string} Members record id
 */
export function createMeeting(projectId, o = {}) {
  const fields = {
    [F.TYPE]:    o.type,
    [F.PROJECT]: [projectId],
    [F.DATE]:    o.meetingDate,
    [F.STATUS]:  'DRAFTING',
  }
  if (o.meetingEnd)        fields[F.END]         = o.meetingEnd
  if (o.location)          fields[F.LOCATION]    = o.location
  if (o.phase)             fields[F.PHASE]       = o.phase
  if (o.attendees && o.attendees.length)     fields[F.ATTENDEES]   = o.attendees
  if (o.consultants && o.consultants.length) fields[F.CONSULTANTS] = o.consultants
  if (o.creatorMemberId)   fields[F.CREATOR]     = [o.creatorMemberId]
  return apiPost(TBL_MEETINGS, fields)
}

/** Patch arbitrary fields on a Meeting (header edits go through here). */
export function updateMeeting(meetingId, fields) {
  return apiPatch(TBL_MEETINGS, meetingId, fields)
}

/**
 * Delete a Meeting record. Threads are NOT deleted — they live on independently and may belong
 * to other meetings; deleting the meeting only drops the meeting↔thread links (Airtable removes
 * them with the record).
 */
export function deleteMeeting(meetingId) {
  return apiDelete(TBL_MEETINGS, meetingId)
}

/** Set the workflow status (DRAFTING / DRAFT READY / DRAFT APPROVED / SKIP MINUTES). */
export function setMeetingStatus(meetingId, status) {
  return updateMeeting(meetingId, { [F.STATUS]: status })
}

/**
 * Mark a meeting as having had its Zoom notes imported + committed. Latches the {Notes Imported}
 * checkbox so the Import Zoom notes action disables itself from then on — re-importing the same
 * summary would create duplicate threads. (Fail-open: if the field doesn't exist yet, the gate
 * simply never trips and import stays available, matching prior behavior.)
 */
export function markMeetingNotesImported(meetingId) {
  return updateMeeting(meetingId, { [F.NOTES_IMPORTED]: true })
}

/* ── Thread ↔ meeting linkage ─────────────────────────────────────────────────
 * The minutes line items are Meetings.Threads. A thread can belong to several meetings (its
 * Meetings link is multi) — that IS the "recurs on the next meeting" mechanism. We patch from
 * the Meeting side; Airtable replaces the whole link array, so add/remove read the current set.
 */

/** Current linked thread ids on a meeting record. */
export function getMeetingThreadIds(meeting) {
  return (meeting && meeting.fields && meeting.fields[F.THREADS]) || []
}

/** Replace the full set of threads linked to a meeting. */
export function setMeetingThreads(meetingId, threadIds) {
  return updateMeeting(meetingId, { [F.THREADS]: threadIds || [] })
}

/** Link one thread onto a meeting (idempotent), given the meeting's current thread ids. */
export function addThreadToMeeting(meetingId, threadId, currentThreadIds) {
  const set = new Set(currentThreadIds || [])
  set.add(threadId)
  return setMeetingThreads(meetingId, Array.from(set))
}

/** Unlink one thread from a meeting (does not delete the thread). */
export function removeThreadFromMeeting(meetingId, threadId, currentThreadIds) {
  // Removed by hand is remembered, so the self-filling agenda never puts it back (database/309).
  return agendaCall('excludeThread', meetingId, { threadId })
    .catch(() => setMeetingThreads(meetingId, (currentThreadIds || []).filter(id => id !== threadId)))
}

/* ── Agenda by who is involved (api/_meetingAgenda.js, Matt 2026-10-19) ──────────────────────────
 * A meeting's agenda = the open threads that involve someone attending (all open threads for an
 * internal meeting). It refreshes until the meeting is held. Each call returns
 * { onAgenda, suggested, missing:[{ party, name, contactIds, threads:[{id,title}] }], internal, held }. */
async function agendaCall(action, meetingId, extra = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try { const t = window.__clerkGetToken ? await window.__clerkGetToken() : null; if (t) headers['Authorization'] = 'Bearer ' + t } catch (_) { /* server rejects */ }
  const res = await fetch('/api/meeting-minutes', { method: 'POST', headers, body: JSON.stringify({ action, meetingId, ...extra }) })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.error) throw new Error(data.error || ('Agenda request failed: ' + res.status))
  return data
}
export const getMeetingAgenda = meetingId => agendaCall('agenda', meetingId)
export const refreshMeetingAgenda = meetingId => agendaCall('refreshAgenda', meetingId)
export const addThreadsToMeeting = (meetingId, threadIds) => agendaCall('addThreads', meetingId, { threadIds })
export const addMeetingAttendees = (meetingId, contactIds) => agendaCall('addAttendees', meetingId, { contactIds })
/** Tie thread activities to the meeting they were recorded at, with the court then (database/310). rows: [{ activityId, threadId, courtContactId, courtName }] */
export const linkMeetingActivities = (meetingId, rows) => agendaCall('linkActivities', meetingId, { rows })

/**
 * Carry forward every still-open thread on the project onto a meeting (the recurring-minutes
 * behavior). Open = not Closed and not flagged 'Exclude from Meeting Notes'. Returns the new
 * full thread-id set written to the meeting.
 *
 * @param {string} meetingId
 * @param {string[]} currentThreadIds   meeting's existing links
 * @param {Array} projectThreads        raw Thread records (from getProjectThreads)
 */
export async function carryForwardOpenThreads(meetingId, currentThreadIds, projectThreads) {
  const openIds = (projectThreads || [])
    .filter(t => {
      const f = t.fields || {}
      const closed = f['Closed'] === true || (f['Status'] || '').includes('Closed')
      const excluded = f['Exclude from Meeting Notes'] === true
      return !closed && !excluded
    })
    .map(t => t.id)
  const set = new Set(currentThreadIds || [])
  openIds.forEach(id => set.add(id))
  const next = Array.from(set)
  await setMeetingThreads(meetingId, next)
  return next
}

/**
 * Carry forward every still-open thread from PRIOR meetings of the same TYPE onto a new meeting —
 * called automatically right after a meeting is created so the discussion continues. A thread's
 * meeting-type association lives in the {Type (from Meetings)} lookup (the types of every meeting
 * it appears on), so "OAC threads" = threads whose lookup includes 'OAC'. Open = not Closed and not
 * 'Exclude from Meeting Notes'.
 *
 * Consultant Coordination is additionally scoped by consultant: a thread only carries if it shares
 * at least one consultant with the new meeting. A thread's consultant set is the
 * {Consultants Involved (from Meetings)} lookup (the union across every meeting it has been on —
 * at this point only PRIOR meetings, since the new one isn't linked yet). This stops, e.g., a
 * Mechanical-only thread from landing on a new Structural-only consultant meeting.
 *
 * @param {string} meetingId   the newly created meeting
 * @param {string} projectId   its project
 * @param {string} meetingType the new meeting's Type (e.g. 'OAC')
 * @param {string[]} consultants the new meeting's Consultants Involved (only used for Consultant Coordination)
 */
export async function carryForwardThreadsByType(meetingId, projectId, meetingType, consultants = []) {
  if (!meetingId || !projectId || !meetingType) return []
  const isConsultant = meetingType === 'Consultant Coordination'
  const want = new Set(consultants || [])
  const threads = await getProjectThreads(projectId)
  const ids = (threads || []).filter(t => {
    const f = t.fields || {}
    const closed = f['Closed'] === true || (f['Status'] || '').includes('Closed')
    const excluded = f['Exclude from Meeting Notes'] === true
    const types = f['Type (from Meetings)'] || []
    const onType = Array.isArray(types) ? types.includes(meetingType) : types === meetingType
    if (closed || excluded || !onType) return false
    if (isConsultant && want.size) {
      const tc = f['Consultants Involved (from Meetings)'] || []
      const arr = Array.isArray(tc) ? tc : [tc]
      return arr.some(c => want.has(c))   // share ≥1 consultant with the new meeting
    }
    return true
  }).map(t => t.id)
  if (ids.length) await setMeetingThreads(meetingId, ids)
  return ids
}

/* ── Post (issue minutes) ─────────────────────────────────────────────────────
 * The heavy lifting (render PDF, attach to Posted Meeting Minutes, email attendees via Resend)
 * runs server-side in api/meeting-minutes.js so the service-role key and pdf-lib stay off the
 * client. This is the single client entry point; it expects a Clerk JWT for auth.
 */

/**
 * Issue a meeting's minutes: server renders the PDF, stores it on Posted Meeting Minutes, sets
 * Status=POSTED + Posted + Posting Member, then (if send=true) emails the PDF to all attendees.
 *
 * @param {string} meetingId
 * @param {object} [opts] - { send:boolean (email attendees), getToken:()=>Promise<string>,
 *                            postingMemberId:string, cc:string[] (FYI copy recipients) }
 * @returns server JSON ({ ok, pdfUrl, emailed, ccd } or { error })
 */
export async function postMeetingMinutes(meetingId, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try {
    if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t }
  } catch (_) { /* unauthenticated → server rejects */ }
  const res = await fetch('/api/meeting-minutes', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      action: 'post', meetingId,
      send: opts.send !== false,
      postingMemberId: opts.postingMemberId || null,
      cc: Array.isArray(opts.cc) ? opts.cc : [],
      repost: !!opts.repost,
      ...(opts.audience ? { audience: opts.audience } : {}),
    }),
  })
  return res.json().catch(() => ({ error: 'Bad response from /api/meeting-minutes' }))
}

/**
 * Who a meeting's minutes will reach (Matt, 2026-10-22): `clientPresent` (a client attended, so they
 * go to the Client Hub), `email` [{ name, email }] (outside attendees with no Client Hub, sent the PDF),
 * `noEmail` [names] (outside attendees with no email on file), `hub` [names]. api/_meetingAgenda.js.
 */
export async function getMinutesAudience(meetingId, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try {
    if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t }
  } catch (_) { /* unauthenticated → server rejects */ }
  const res = await fetch('/api/meeting-minutes', { method: 'POST', headers, body: JSON.stringify({ action: 'audience', meetingId }) })
  const j = await res.json().catch(() => null)
  if (!res.ok || !j || j.error) throw new Error((j && j.error) || 'Could not read who the minutes go to.')
  return j
}

/**
 * Put freshly posted minutes in the project's Client Hub as a `meeting_minutes` entry. Publishing
 * it emails the Hub's subscribers the minutes notice (api/journey-admin.js notifyEntryPublished);
 * `post` is postMeetingMinutes' result, whose revision date and meeting label go on the entry so
 * the notice, the Hub page and the reviewer's triage all read the same date. Reposting the same
 * meeting updates its entry instead of adding a second one. Throws on failure.
 * @param {{ meetingId, projectId, projectName, published:boolean, post:object, postedBy:{memberId,name} }} args
 */
export async function addMinutesToClientHub({ meetingId, projectId, projectName, published, post, postedBy }) {
  const meeting = await getMeetingById(meetingId)
  const rawPhase = (meeting && meeting.fields[F.PHASE]) || ''
  // Meetings.Phase is a shorthand select ("Schematic Design"), not the project's Phase record name
  // the Hub groups by ("Schematic Design Phase"); resolve it, or the entry lands out of order.
  let phase = rawPhase
  if (rawPhase && projectId) {
    try {
      const phases = await getProjectPhases(projectId)
      phase = matchPhaseName(rawPhase, (phases || []).map(p => p.fields && p.fields['Phase Name']))
    } catch (_) { /* the raw value */ }
  }
  const { journey } = await getOrCreateLink(projectId, projectName)
  let existingId = null
  try {
    const { entries } = await listEntries(journey.id)
    const existing = (entries || []).find(e => e.type === 'meeting_minutes' && e.meeting_airtable_id === meetingId)
    if (existing) existingId = existing.id
  } catch (_) { /* insert */ }
  const p = post || {}
  await upsertEntry({
    id: existingId || undefined,
    journeyId: journey.id,
    type: 'meeting_minutes',
    title: null,
    data: {
      phase,
      entryDate: (meeting && meeting.fields[F.DATE]) || '',
      revisionDue: p.revisionDue || '', revisionDueLabel: p.revisionDueLabel || '',
      meetingDateStr: p.meetingDateStr || '', meetingType: p.meetingType || '',
      postedByMemberId: (postedBy && postedBy.memberId) || null, postedByName: (postedBy && postedBy.name) || null,
    },
    meetingAirtableId: meetingId,
    published: !!published,
  })
  window.dispatchEvent(new CustomEvent('journey-updated', { detail: { journeyId: journey.id } }))
  return journey.id
}

/**
 * Preview what the post-and-email step will send WITHOUT posting: the exact subject + body, the
 * resolved attendee recipients (with a flag for any attendee missing an email), and the attachment
 * name. Powers the confirm modal so we can verify everyone has an address and add FYI CCs.
 * Returns { ok, subject, lines, attendees:[{name,email}], emails:[...], type, dateStr, projectName,
 * attachmentName } or { error }.
 */
export async function previewMeetingEmail(meetingId, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try { if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t } }
  catch (_) { /* server rejects unauthenticated */ }
  try {
    const res = await fetch('/api/meeting-minutes', {
      method: 'POST', headers, body: JSON.stringify({ action: 'previewEmail', meetingId }),
    })
    return res.json().catch(() => ({ error: 'Bad response from /api/meeting-minutes' }))
  } catch (e) { return { error: (e && e.message) || 'Preview request failed.' } }
}

/**
 * Render the working-draft PDF without posting — used by the in-app Preview action. The endpoint
 * returns the PDF binary (a "WORKING DRAFT" document); we wrap it in an object URL the caller opens.
 * Returns { pdfUrl } or { error }; does not change workflow status.
 */
export async function previewMeetingMinutes(meetingId, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try {
    if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t }
  } catch (_) { /* preview is best-effort */ }
  try {
    const res = await fetch('/api/meeting-minutes-pdf', {
      // overlay: a meeting-notes import's draft, shown as if committed (api/meeting-minutes-pdf.js).
      method: 'POST', headers, body: JSON.stringify({ meetingId, working: true, overlay: opts.overlay || null }),
    })
    if (!res.ok) { const j = await res.json().catch(() => ({})); return { error: j.error || ('Preview failed (' + res.status + ')') } }
    const blob = await res.blob()
    return { pdfUrl: URL.createObjectURL(blob) }
  } catch (e) {
    return { error: (e && e.message) || 'Preview request failed' }
  }
}

/**
 * Parse a pasted Zoom "Meeting Assets" summary into proposed thread updates / new threads (no writes).
 * Returns { suggestions, threads, categories, meetingDay } for the review modal, or { error }.
 * @param {object} [opts] - { getToken:()=>Promise<string> }
 */
export async function parseMeetingNotes(meetingId, text, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try { if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t } }
  catch (_) { /* server rejects unauthenticated */ }
  try {
    const res = await fetch('/api/meeting-notes-parse', { method: 'POST', headers, body: JSON.stringify({ meetingId, text }) })
    return res.json().catch(() => ({ error: 'Bad response from the parser.' }))
  } catch (e) { return { error: (e && e.message) || 'Parse request failed.' } }
}

/** Off-agenda threads researched against the pasted notes (api/meeting-notes-parse.js#checkOffAgenda):
 *  [{ id, title, about, court, lastMeeting, involvedHere, verdict, quote, said, note }]. Writes nothing. */
export async function checkOffAgendaThreads(meetingId, text) {
  const headers = { 'Content-Type': 'application/json' }
  try { const t = window.__clerkGetToken ? await window.__clerkGetToken() : null; if (t) headers['Authorization'] = 'Bearer ' + t } catch (_) { /* server rejects */ }
  const res = await fetch('/api/meeting-notes-parse', { method: 'POST', headers, body: JSON.stringify({ action: 'checkOffAgenda', meetingId, text }) })
  const d = await res.json().catch(() => ({}))
  if (!res.ok || d.error) throw new Error(d.error || 'Check failed')
  return d.threads || []
}

/* ── Zoom notes import: save/resume + review gate ─────────────────────────────
 * The parsed suggestions persist as a draft (Supabase `meeting_notes_drafts`, one active row per
 * meeting) so the import can be left and resumed, and so committing to threads is a deliberate
 * second step (draft -> ready_for_review -> committed) instead of happening in the same sitting
 * as the paste. See docs/MEETING_MINUTES.md.
 */

async function draftsApi(action, params, opts = {}) {
  const headers = { 'Content-Type': 'application/json' }
  try { if (opts.getToken) { const t = await opts.getToken(); if (t) headers['Authorization'] = 'Bearer ' + t } }
  catch (_) { /* server rejects unauthenticated */ }
  try {
    const res = await fetch('/api/meeting-notes-drafts', { method: 'POST', headers, body: JSON.stringify({ action, ...params }) })
    return res.json().catch(() => ({ error: 'Bad response from the server.' }))
  } catch (e) { return { error: (e && e.message) || 'Request failed.' } }
}

/** The active (non-committed) Zoom-notes draft for a meeting, or { draft: null }. */
export function getMeetingNotesDraft(meetingId, opts = {}) {
  return draftsApi('get', { meetingId }, opts)
}

/** Every active draft for a project, for the meetings-list badge — keyed by meeting_id client-side. */
export function getProjectMeetingNotesDrafts(projectId, opts = {}) {
  return draftsApi('listForProject', { projectId }, opts)
}

/**
 * Save (create or update) the working draft. Safe to call repeatedly as the reviewer edits
 * suggestions — this is the actual "leave and come back later" mechanism. Rejected server-side if
 * the draft is no longer in 'draft' status (i.e. already submitted).
 */
export function saveMeetingNotesDraft(fields, opts = {}) {
  return draftsApi('save', fields, opts)
}

/**
 * Mark a draft ready for review — locks it (server-enforced) until reopened. `reviewer`
 * ({ memberId, name }) is required: it is who the daily triage asks to review it.
 */
export function submitMeetingNotesDraft(id, { submittedByName, submittedByMemberId, reviewer }, opts = {}) {
  return draftsApi('submit', {
    id, submittedByName, submittedByMemberId,
    reviewerMemberId: reviewer && reviewer.memberId, reviewerName: reviewer && reviewer.name,
  }, opts)
}

/** The pre-filled reviewer for a project's imports (its Client Manager): { reviewer: {memberId,name} | null }. */
export function getDefaultMeetingNotesReviewer(projectId, opts = {}) {
  return draftsApi('defaultReviewer', { projectId }, opts)
}

/** Reopen a 'ready_for_review' draft for further edits. The reopener becomes the one who finishes it. */
export function reopenMeetingNotesDraft(id, { reopenedByName, reopenedByMemberId }, opts = {}) {
  return draftsApi('reopen', { id, reopenedByName, reopenedByMemberId }, opts)
}

/** Send a 'ready_for_review' draft back to whoever drafted it, with a note saying what to fix. */
export function sendBackMeetingNotesDraft(id, { note, sentBackByName, sentBackByMemberId, returnToMemberId, returnToName }, opts = {}) {
  return draftsApi('sendBack', { id, note, sentBackByName, sentBackByMemberId, returnToMemberId, returnToName }, opts)
}

/** Close out a draft after its suggestions have actually been committed to threads/activities. */
export function commitMeetingNotesDraft(id, committedByName, opts = {}) {
  return draftsApi('commit', { id, committedByName }, opts)
}

/** Abandon an unsubmitted draft (e.g. a bad paste) so a fresh import can start clean. */
export function discardMeetingNotesDraft(id, opts = {}) {
  return draftsApi('discard', { id }, opts)
}

/** Deep link into a meeting's minutes zoom — mirrors threadDeepLink in services/threads.js. */
export function meetingDeepLink(projectId, meetingId) {
  const obj = { p: projectId, r: 'meetings', sec: 'meeting-detail:' + meetingId }
  let z = ''
  try { z = btoa(JSON.stringify(obj)) } catch (_) { /* ignore */ }
  const params = ['recordId=' + encodeURIComponent(projectId)]
  if (z) params.push('z=' + encodeURIComponent(z))
  return window.location.origin + '/project?' + params.join('&')
}

/**
 * Ping the meeting's own Slack discussion that Zoom notes are ready for review — starts the
 * discussion if the meeting has none yet.
 * Best-effort; never blocks the submit that triggered it.
 */
export async function notifyMeetingNotesReadyForReview(meetingId, { submitterName, itemCount }, opts = {}) {
  try {
    const meeting = await getMeetingById(meetingId)
    if (!meeting) return
    const f = meeting.fields || {}
    const projectId = (f[F.PROJECT] || [])[0]
    if (!projectId) return
    const project = await getProjectDetails(projectId)
    const projectName = (project && project.fields && project.fields['Project Name']) || ''
    const entityName = (f[F.TYPE] || 'Meeting') + ' — ' + (f[F.DATE_STR] || '')
    const deepLink = meetingDeepLink(projectId, meetingId)
    const token = opts.getToken ? await opts.getToken().catch(() => null) : null
    const n = itemCount || 0
    const message = `📝 Zoom notes ready for review — *${n} item${n === 1 ? '' : 's'}* from the ${f[F.TYPE] || 'meeting'} on ${f[F.DATE_STR] || ''}${submitterName ? ' (by ' + submitterName + ')' : ''}\n<${deepLink}|Review in AlderCortex →>`

    let ts = f[F.SLACK_TS]
    if (!ts) {
      const res = await startDiscussion({ entityId: meetingId, entityType: 'meeting', entityName, projectId, projectName, startedBy: submitterName, deepLink }, token)
      if (!res || res.error || !res.ts) return
      ts = res.ts
    }
    await replyToThread({ projectId, threadTs: ts, message, authorName: submitterName }, token)
  } catch (_) { /* non-fatal — Slack hiccups never block a submit */ }
}

/* ── Display helpers (presentation logic the components share) ─────────────────── */

/** Normalize a Meeting record to the fields the UI needs. */
export function meetingDisplay(meeting) {
  const f = (meeting && meeting.fields) || {}
  return {
    id:           meeting && meeting.id,
    type:         f[F.TYPE] || '',
    date:         f[F.DATE] || '',
    dateDay:      dayOf(f[F.DATE]),
    dateStr:      f[F.DATE_STR] || '',
    end:          f[F.END] || '',
    location:     f[F.LOCATION] || '',
    phase:        f[F.PHASE] || '',
    status:       f[F.STATUS] || '',
    statusMsg:    f[F.STATUS_MSG] || f[F.STATUS] || '',
    stage:        typeof f[F.STAGE] === 'number' ? f[F.STAGE] : 1,
    posted:       f[F.POSTED] === true,
    notesImported: f[F.NOTES_IMPORTED] === true,
    skip:         f[F.SKIP] === true,
    threadIds:    f[F.THREADS] || [],
    attendeeIds:  f[F.ATTENDEES] || [],
    attendeeEmails: f[F.ATTENDEE_EMAILS] || [],
    consultants:  f[F.CONSULTANTS] || [],
    postedPdfUrl: (f[F.POSTED_PDF] && f[F.POSTED_PDF][0] && f[F.POSTED_PDF][0].url) || '',
  }
}

/** Group meetings by Type into ordered { type, meetings } buckets (MEETING_TYPES order; unknowns last). */
export function groupMeetingsByType(meetings) {
  const groups = {}
  const seen = []
  ;(meetings || []).forEach(m => {
    const type = (m.fields && m.fields[F.TYPE]) || 'Other'
    if (!groups[type]) { groups[type] = []; seen.push(type) }
    groups[type].push(m)
  })
  const order = t => { const i = MEETING_TYPES.indexOf(t); return i === -1 ? 99 : i }
  seen.sort((a, b) => { const d = order(a) - order(b); return d !== 0 ? d : (a < b ? -1 : 1) })
  return seen.map(type => ({ type, meetings: groups[type] }))
}

/**
 * Filter + tag a thread's activities for minutes rendering: keep only the three minutes types,
 * newest first, and flag the entry whose Activity Date matches the meeting date as the current
 * meeting's activity (rendered bold in the PDF and emphasized in the UI).
 *
 * @param {Array} activities       Thread Activity records ({ id, fields }) for one thread
 * @param {string} meetingDateISO  the meeting's date (any ISO; compared by day)
 */
export function minutesActivities(activities, meetingDateISO) {
  const md = dayOf(meetingDateISO)
  return (activities || [])
    .filter(a => MINUTES_ACTIVITY_TYPES.includes((a.fields && a.fields['Activity Type']) || ''))
    .map(a => {
      const f = a.fields || {}
      return {
        id:        a.id,
        type:      f['Activity Type'] || '',
        date:      dayOf(f['Activity Date']),
        notes:     f['Notes'] || '',
        ballInCourt: (f['Name (from Ball In Court)'] || [])[0] || '',
        isCurrent: !!md && dayOf(f['Activity Date']) === md,
      }
    })
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0)) // newest first
}
